<template>
  <global-theme-provider :theme="globalApplicationTheme" v-loading="initialLoading">
    <application-wrapper v-show="pageReady" id="scrollbar">
      <div id="app" :class="{ reset: resetInitialAppStyle }">
        <test-mode-stripe v-if="isTestMode" />
        <basic-app-layout v-if="globalError === null">
          <router-view />
        </basic-app-layout>

        <error-page-layout v-else :error-data="globalError" />

        <messages-handler />
        <transition name="transition-language">
          <styled-language-modal v-show="showLangDialog" />
        </transition>
      </div>
    </application-wrapper>
  </global-theme-provider>
</template>

<script>
import ModalLanguagePicker from '@/components/UI/ModalLanguagePicker';

import ErrorPageLayout from '@/layouts/ErrorPageLayout';
import BasicAppLayout from '@/layouts/BasicAppLayout';

import MessagesHandler from '@/components/UI/MessagesController';

import iframeDetection from '@/mixins/iframeDetection';
import { mapActions, mapGetters, mapState } from 'vuex';

import styled from 'vue-styled-components';

// TODO: refactor CSS as a CSS in JS for easely theming, etc.
// Styled components
import { ThemeProvider } from 'vue-styled-components';
import themes from '@/config/themes';
import Cookies from '@/services/Cookies';
import MerchantSDK from '@paycore/merchant-sdk-js';
import customStylesHandler from '@/mixins/customStylesHandler';
import convertStrToBoolean from '@/helpers/convertStrToBoolean';
import parseAvailableLocales from '@/helpers/parseAvailableLocales';

const CRPT_SERVICE = ['bitcoin', 'bitcoin_cash', 'litecoin'];

const styledLanguageModal = styled(ModalLanguagePicker)`
  .modal {
    background: var(--primary_background_color);
  }
`;

const testModeStripe = styled.section`
  position: fixed;
  top: 0;
  width: 100%;
  left: 0;
  background: repeating-linear-gradient(-45deg, #f3c924, #f3c924 10px, #2d3440 0, #2d3440 20px);
  height: 5px;
  z-index: 999;
`;

// Just after the body tag, our theme provider.
const globalThemeProvider = styled(ThemeProvider)`
  width: 100%;
  height: 100%;
  position: relative;
`;

const ApplicationWrapper = styled.section`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: center;
  left: 0;
  height: 100%;
`;

export default {
  name: 'App',
  data() {
    return {
      themes,
      parameters: {},
      showLangDialog: false,
      initialLoading: false,
      pageReady: false,
    };
  },
  async created() {
    this.initStyles();
    /**
     * Entire initialize block wrapped in try/catch,
     * if something throw an error - app will be redirected to error page with details.
     */
    try {
      this.initialLoading = true;
      const {
        currency,
        public_key,
        amount,
        description = null,
        service = null,
        service_fields = null,
        cpi = null,
        expires = null,
        theme = null,
        locale = null,
        display = null,
        style = null,
        available_locales = null,
      } = this.getQueryParameters();

      const { id } = this.$route.params;

      const cpiValue = cpi || id;

      await this.getCheckoutOptions({
        params: {
          ...(cpiValue ? { cpi: cpiValue } : {}),
          ...(public_key ? { public_key } : {}),
        },
      });

      /**
       * Init application -
       * amount, description, public_key, commerce_account data, themes and styling;
       *  */
      // Store actions
      if (locale) {
        this.setQueryLocale(locale);
      }
      if (available_locales) {
        this.setAvailableLocales(available_locales);
      }

      const defaultAvailableLocales = ['en', 'ru', 'uk', 'sw', 'ja', 'zh', 'zh-hant'];

      const defaultLocales = () => {
        return this.$i18n.locale === 'en' ? ['en'] : [this.$i18n.locale, 'en'];
      };

      const optionLocales = parseAvailableLocales(
        available_locales,
        this.checkoutOptions?.available_locales,
        defaultAvailableLocales
      );

      const selectedLocales = locale || this.checkoutOptions?.locale;

      const conditionForSetLocale = locale =>
        optionLocales.includes(locale) || defaultLocales().includes(locale);

      if (conditionForSetLocale(selectedLocales)) {
        this.$i18n.locale = selectedLocales;
      } else if (optionLocales.length && !optionLocales.includes(this.$i18n.locale)) {
        this.$i18n.locale = optionLocales[0] || 'en';
      } else if (!defaultLocales().includes(this.$i18n.locale)) {
        this.$i18n.locale = defaultLocales()[0] || 'en';
      }

      if (style != null || this.checkoutOptions !== null) {
        const data = {
          theme: this.checkoutOptions.theme,
          ...(style || {}),
        };

        this.setUserThemeOptions(data);
        this.setUITheme(data.theme);
        this.setDefaultTheme(data.theme);
      }

      if (display !== null || this.checkoutOptions !== null) {
        const {
          layout_hide_header,
          layout_hide_footer,
          layout_hide_progress_bar,
          layout_hide_lifetime_counter,
          invoice_hide_lifetime_counter,
          layout_hide_method_filter,
        } = this.checkoutOptions;

        const layoutSettings = {
          hide_header: layout_hide_header,
          hide_footer: layout_hide_footer,
          hide_progress_bar: layout_hide_progress_bar,
          hide_lifetime_counter: layout_hide_lifetime_counter,
          hide_invoice_lifetime_counter: invoice_hide_lifetime_counter,
          hide_method_filter: layout_hide_method_filter,
        };

        const layoutData = {
          ...Object.keys(layoutSettings).reduce((acc, key) => {
            if (layoutSettings[key] !== null) {
              acc[key] = layoutSettings[key];
            }
            return acc;
          }, {}),
          ...(display || {}),
        };

        Object.keys(layoutData).forEach(key => {
          layoutData[key] = convertStrToBoolean(layoutData[key]);
        });

        this.setLayoutSettings(layoutData);
      }

      this.initBus();
      this.getScreenSizeVST();
    } catch (e) {
      console.error('error while initialize', e);
    } finally {
      this.initialLoading = false;
      this.pageReady = true;
    }
  },

  computed: {
    ...mapState([
      'categories',
      'publicKey',
      'invoiceInitialData',
      'globalError',
      'paymentInvoiceData',
      'checkoutOptions',
    ]),
    ...mapState('UI', ['layoutConfig']),
    ...mapGetters('UI', ['globalApplicationTheme']),

    paymentInvoice() {
      return this.paymentInvoiceData ? this.paymentInvoiceData.data || this.paymentInvoiceData : {};
    },

    resetInitialAppStyle() {
      if (!Object.keys(this.paymentInvoice).length) return false;
      return CRPT_SERVICE.includes(this.paymentInvoice.service_method);
    },

    isTestMode() {
      return (
        this.invoiceInitialData.public_key &&
        this.invoiceInitialData.public_key.indexOf('test') != -1
      );
    },
  },

  components: {
    ErrorPageLayout,
    BasicAppLayout,
    ApplicationWrapper,
    MessagesHandler,
    globalThemeProvider,
    styledLanguageModal,
    testModeStripe,
  },
  methods: {
    ...mapActions(['setUITheme', 'processApiKey', 'setInitState', 'getCheckoutOptions']),
    ...mapActions('UI', [
      'setDefaultTheme',
      'setUserThemeOptions',
      'setLayoutSettings',
      'setAvailableLocales',
      'setQueryLocale',
    ]),

    initBus() {
      // TODO: Locale also may be passed from query param;
      this.$bus.$on('changeLocale', $event => {
        Cookies.set('user_language', $event, { path: '/' });
        this.$i18n.locale = $event;
      });

      this.$bus.$on('LangDialogToggle', $event => {
        console.log('Bus handled', $event);
        this.showLangDialog = $event ? true : false;
      });
    },

    // FIXME: Depracated methods below;
    // Will be deleted when all app will be moved on styled components

    addStyles(el, styles) {
      el.appendChild(document.createTextNode(styles));
    },

    initStyles() {
      let style = document.createElement('style');
      style.type = 'text/css';
      this.addStyles(style, this.stylesToHead);
      this.addStyles(style, this.mediaStyles);

      document.head.appendChild(style);
    },
  },

  mixins: [iframeDetection, customStylesHandler],
};
</script>

<style>
body {
  margin: 0;
  background: var(--background);
  color: var(--on_background);
  font-family: var(--font-family);
}
#app {
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-align: center;
  color: #2c3e50;
  justify-content: space-between;
  position: relative;

  display: flex;
  flex-direction: column;
}

#app.reset {
  margin: 0 !important;
  width: 100% !important;
}
</style>
